import { useEffect, useRef } from 'react';
import { Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import clsx from 'clsx';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from './mapUtils';

/**
 * Leaflet SVG "marching ants" animation used for BOUNDARY geofences.
 * Updates the `dashOffset` style on each animation frame.
 */
export function MarchingPolygon({
  positions,
  pathOptions,
  speed = 18,
  children,
}: {
  positions: [number, number][];
  pathOptions: any;
  speed?: number;
  children?: React.ReactNode;
}) {
  const polyRef = useRef<L.Polygon | null>(null);

  useEffect(() => {
    let raf = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const layer = polyRef.current;
      if (layer) {
        const seconds = (now - start) / 1000;
        const offset = -(seconds * speed);
        layer.setStyle({ dashOffset: `${offset}` } as any);
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [speed]);

  return (
    <Polygon ref={polyRef as any} positions={positions} pathOptions={pathOptions}>
      {children}
    </Polygon>
  );
}

// Recenter button: fit ALL operational elements (platoons + farmbases +
// targets + planned geofences) into view. `onRecenter` lets the page reset its
// user-interaction latch so auto-fit re-arms (F-6).
export function MapCenterControl({
  points,
  onRecenter,
  paddingBottomRight = [60, 60],
}: {
  points: [number, number][];
  onRecenter?: () => void;
  paddingBottomRight?: [number, number];
}) {
  const map = useMap();

  return (
    <button
      onClick={() => {
        onRecenter?.();
        if (points.length > 0) {
          try {
            map.fitBounds(L.latLngBounds(points), {
              paddingTopLeft: [60, 60],
              paddingBottomRight,
              maxZoom: 15,
              animate: true,
            });
            return;
          } catch {
            // fall through to default view
          }
        }
        map.setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, { animate: true });
      }}
      className={clsx(
        // On mobile sits above the bottom chip bar; desktop keeps bottom-4.
        'absolute bottom-[4.25rem] lg:bottom-4 left-1/2 transform -translate-x-1/2 z-[1000]',
        'px-4 py-2 min-h-11 lg:min-h-0 bg-gray-900/90 border border-gray-700 rounded-lg text-sm text-gray-300',
        'hover:bg-gray-800 transition-colors flex items-center gap-2 touch-manipulation'
      )}
    >
      <span>🎯</span>
      Recenter
    </button>
  );
}

// Fly to a selected location.
export function FlyToLocation({ position, zoom }: { position: [number, number] | null; zoom?: number }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, zoom || 15, { duration: 1 });
  }, [position, map, zoom]);
  return null;
}

// Initial view (runs once on mount / map remount).
export function SetInitialView({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // once
  return null;
}

// Keep Leaflet layout stable when the main sidebar opens/closes.
export function InvalidateMapSizeOnSidebar({ sidebarOpen }: { sidebarOpen: boolean }) {
  const map = useMap();
  useEffect(() => {
    const t = window.setTimeout(() => {
      try {
        map.invalidateSize({ animate: false, pan: false });
      } catch {
        // ignore
      }
    }, 350);
    return () => window.clearTimeout(t);
  }, [sidebarOpen, map]);
  return null;
}

// F-6: auto-fit the view over ALL operational elements (platoons + farmbases +
// targets). Fits on first data, then re-fits ONLY when the element set changes
// (fitKey) AND the user hasn't panned/zoomed since the last auto-fit. The
// page-owned `userInteractedRef` latch is reset by the Recenter button.
export function OperationalAutoFit({
  points,
  fitKey,
  fallbackPoints,
  userInteractedRef,
  paddingTopLeft = [60, 60],
  paddingBottomRight = [60, 60],
}: {
  points: [number, number][];
  fitKey: string;
  fallbackPoints?: [number, number][];
  userInteractedRef: React.MutableRefObject<boolean>;
  // Asymmetric padding keeps fitted elements clear of overlaid panels
  // (e.g. the desktop right-side Platoons/Alerts stack).
  paddingTopLeft?: [number, number];
  paddingBottomRight?: [number, number];
}) {
  const map = useMap();
  const prevKey = useRef<string | null>(null);
  const fittingRef = useRef(false);

  // Latch user gestures. `dragstart` only fires for user drags; `zoomstart`
  // also fires for OUR programmatic fits, so suppress it while fitting.
  useEffect(() => {
    const onDragStart = () => {
      userInteractedRef.current = true;
    };
    const onZoomStart = () => {
      if (!fittingRef.current) userInteractedRef.current = true;
    };
    map.on('dragstart', onDragStart);
    map.on('zoomstart', onZoomStart);
    return () => {
      map.off('dragstart', onDragStart);
      map.off('zoomstart', onZoomStart);
    };
  }, [map, userInteractedRef]);

  useEffect(() => {
    if (prevKey.current === fitKey) return;
    const pts = points.length > 0 ? points : fallbackPoints ?? [];
    if (pts.length === 0) return; // nothing to fit yet — wait for data
    // The element set changed but the operator moved the map: absorb the change
    // without yanking the view.
    if (userInteractedRef.current && prevKey.current !== null) {
      prevKey.current = fitKey;
      return;
    }
    prevKey.current = fitKey;
    fittingRef.current = true;
    try {
      if (pts.length === 1) {
        map.setView(pts[0], 14, { animate: false });
      } else {
        map.fitBounds(L.latLngBounds(pts), {
          paddingTopLeft,
          paddingBottomRight,
          maxZoom: 15,
          animate: false,
        });
      }
    } catch (e) {
      console.error('Operational auto-fit failed:', e);
    }
    const t = window.setTimeout(() => {
      fittingRef.current = false;
    }, 400);
    return () => window.clearTimeout(t);
  }, [fitKey, points, fallbackPoints, map, userInteractedRef, paddingTopLeft, paddingBottomRight]);

  return null;
}

// Fit map to the supplied points once per stable key (planned geofences).
export function FitToPoints({ points, fitKey }: { points: [number, number][]; fitKey: string }) {
  const map = useMap();
  const prevKey = useRef<string>('');

  useEffect(() => {
    if (points.length === 0) return;
    if (prevKey.current === fitKey) return;
    prevKey.current = fitKey;
    try {
      map.fitBounds(L.latLngBounds(points), {
        paddingTopLeft: [40, 40],
        paddingBottomRight: [40, 40],
        maxZoom: 16,
        animate: false,
      });
    } catch (e) {
      console.error('Error fitting bounds:', e);
    }
  }, [points, fitKey, map]);

  return null;
}

// Re-fit after the sidebar transition completes (needs invalidateSize first).
// When `userInteractedRef` is supplied and latched, only invalidate size — never
// yank the view away from where the operator panned.
export function RefitOnSidebarChange({
  sidebarOpen,
  points,
  userInteractedRef,
}: {
  sidebarOpen: boolean;
  points: [number, number][];
  userInteractedRef?: React.MutableRefObject<boolean>;
}) {
  const map = useMap();
  const prev = useRef<boolean | null>(null);

  useEffect(() => {
    if (prev.current === null) {
      prev.current = sidebarOpen;
      return;
    }
    if (prev.current === sidebarOpen) return;
    prev.current = sidebarOpen;

    const t = window.setTimeout(() => {
      try {
        map.invalidateSize({ animate: false, pan: false });
      } catch {
        // ignore
      }
      if (points.length === 0) return;
      if (userInteractedRef?.current) return; // operator moved the map — keep their view
      try {
        map.fitBounds(L.latLngBounds(points), {
          paddingTopLeft: [40, 40],
          paddingBottomRight: [40, 40],
          maxZoom: 16,
          animate: false,
        });
      } catch (e) {
        console.error('Error re-fitting after sidebar change:', e);
      }
    }, 360);

    return () => window.clearTimeout(t);
  }, [sidebarOpen, points, map]);

  return null;
}
